import { Button, Card, TextField } from '@starter/ui';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { authClient } from '../lib/auth.ts';

function ForgotPassword() {
  const [sent, setSent] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPending(true);
    setError(null);

    const email = String(new FormData(event.currentTarget).get('email') ?? '');
    // `redirectTo` is where the emailed link lands *after* the API has checked
    // the token, so the form below only ever sees a token it can use.
    const { error: requestError } = await authClient.requestPasswordReset({
      email,
      redirectTo: '/reset-password',
    });

    setPending(false);
    if (requestError) {
      setError(requestError.message ?? 'Could not send the link');
      return;
    }
    setSent(true);
  }

  return (
    <div className="mx-auto max-w-sm">
      <Card title="Reset your password">
        {sent ? (
          <div className="flex flex-col gap-4">
            {/* Deliberately not "we found that account": whether an address is
                registered is not something this form gets to disclose. */}
            <p className="text-sm text-ink-muted" data-testid="reset-requested">
              If that address has an account, a link is on its way. It is valid for one hour.
            </p>
            <Link to="/login" search={{}} className="text-sm text-ink-muted underline">
              Back to sign in
            </Link>
          </div>
        ) : (
          <form className="flex flex-col gap-4" onSubmit={onSubmit}>
            <TextField
              label="Email"
              name="email"
              type="email"
              required
              autoComplete="email"
              data-testid="email"
            />

            {error ? (
              <p className="text-sm text-danger-text" role="alert" data-testid="auth-error">
                {error}
              </p>
            ) : null}

            <Button type="submit" disabled={pending} data-testid="submit">
              {pending ? 'Sending…' : 'Send the link'}
            </Button>

            <Link to="/login" search={{}} className="text-sm text-ink-muted underline">
              Back to sign in
            </Link>
          </form>
        )}
      </Card>
    </div>
  );
}

export const Route = createFileRoute('/forgot-password')({ component: ForgotPassword });
